import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, Shield, LayoutDashboard, Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

const LOGO = process.env.REACT_APP_LOGO_URL || '/logo.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => { setDropOpen(false); setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  const navLink = (to, label, testId) => {
    const active = location.pathname.startsWith(to);
    return (
      <Link to={to} data-testid={testId}
        className={`relative font-rajdhani font-semibold tracking-widest uppercase text-sm transition-colors duration-200 pb-1
          ${active ? 'nav-active' : 'text-rm-muted hover:text-white'}`}>
        {label}
      </Link>
    );
  };

  return (
    <nav
      className={`sticky top-0 z-30 transition-all duration-300 ${scrolled
        ? 'glass-dark border-b border-white/5 shadow-[0_4px_24px_rgba(0,0,0,0.5)]'
        : 'bg-rm-bg/95 border-b border-rm-border'}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group" data-testid="navbar-logo">
          <img src={LOGO} alt="RobloxMart" className="h-8 w-auto max-w-[120px] object-contain transition-transform duration-300 group-hover:scale-110" />
          <span className="font-rajdhani font-bold text-[1.1rem] tracking-wider text-white hidden lg:block">
            ROBLOX<span className="text-rm-red">MART</span>
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6 ml-2">
          {navLink('/browse', 'Browse', 'nav-browse')}
          {navLink('/links', 'Links & FAQ', 'nav-links')}
          {navLink('/feedback', 'Support', 'nav-feedback')}
          {navLink('/donate', 'Donate', 'nav-donate')}
        </div>

        {/* Auth buttons */}
        <div className="hidden md:flex items-center gap-2 shrink-0 ml-auto">
          {/* Theme Toggle */}
          <button onClick={toggleTheme} data-testid="theme-toggle"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            className="w-9 h-9 flex items-center justify-center rounded-sm border border-rm-border text-rm-muted hover:text-white hover:border-rm-red/50 transition-all duration-200">
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {!user ? (
            <>
              <Link to="/auth" data-testid="nav-login" className="btn-outline px-5 py-2 text-sm rounded-sm">Login</Link>
              <Link to="/auth?tab=register" data-testid="nav-register" className="btn-primary px-5 py-2 text-sm rounded-sm">Register</Link>
            </>
          ) : (
            <div className="relative">
              <button onClick={() => setDropOpen(!dropOpen)} data-testid="nav-user-menu"
                className="flex items-center gap-2 px-3 py-2 bg-rm-card border border-rm-border rounded-sm hover:border-rm-red/50 transition-all duration-200 group">
                <div className="w-6 h-6 bg-rm-red/20 rounded-sm flex items-center justify-center">
                  <span className="font-rajdhani font-bold text-[11px] text-rm-red">{user.username?.[0]?.toUpperCase()}</span>
                </div>
                <span className="font-rajdhani font-semibold text-sm text-white">{user.username}</span>
                <ChevronDown size={13} className={`text-rm-muted transition-transform duration-200 ${dropOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-[#111] border border-[#222] rounded-sm shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-40 animate-scale-in overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#222]">
                    <p className="text-xs text-rm-muted">Signed in as</p>
                    <p className="text-sm font-semibold text-white truncate">{user.email}</p>
                  </div>
                  {(user.role === 'seller' || user.role === 'admin') && (
                    <Link to="/dashboard" onClick={() => setDropOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-rm-muted hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="nav-dashboard">
                      <LayoutDashboard size={14} className="text-rm-red" /> Dashboard
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setDropOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-rm-muted hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="nav-admin">
                      <Shield size={14} className="text-rm-red" /> Admin Panel
                    </Link>
                  )}
                  {user.role === 'seller' ? (
                    <Link to={`/${user.username}`} onClick={() => setDropOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-rm-muted hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="nav-profile">
                      <User size={14} className="text-rm-red" /> My Profile
                    </Link>
                  ) : (
                    <Link to="/account-settings" onClick={() => setDropOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-rm-muted hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="nav-account-settings">
                      <User size={14} className="text-rm-red" /> Account Settings
                    </Link>
                  )}
                  <div className="border-t border-[#222]" />
                  <button onClick={handleLogout} data-testid="nav-logout"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-rm-muted hover:text-rm-red hover:bg-rm-red/5 transition-colors">
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle"
          className="md:hidden w-9 h-9 flex items-center justify-center text-rm-muted hover:text-white transition-colors ml-auto">
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden glass-dark border-t border-white/5 px-4 py-5 space-y-1 animate-fade-in">
          {[
            { to: '/browse', label: 'Browse', testId: 'nav-browse-mobile' },
            { to: '/links', label: 'Links & FAQ', testId: 'nav-links-mobile' },
            { to: '/feedback', label: 'Support', testId: 'nav-support-mobile' },
            { to: '/donate', label: 'Donate', testId: 'nav-donate-mobile' },
          ].map(({ to, label, testId }) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)} data-testid={testId}
              className={`block font-rajdhani font-semibold uppercase text-sm py-3 px-2 border-b border-white/5 transition-colors
                ${location.pathname.startsWith(to) ? 'text-rm-red' : 'text-rm-muted hover:text-white'}`}>
              {label}
            </Link>
          ))}
          {!user ? (
            <div className="flex gap-3 pt-3">
              <Link to="/auth" onClick={() => setMenuOpen(false)} className="btn-outline px-4 py-2 text-sm rounded-sm flex-1 text-center">Login</Link>
              <Link to="/auth?tab=register" onClick={() => setMenuOpen(false)} className="btn-primary px-4 py-2 text-sm rounded-sm flex-1 text-center">Register</Link>
            </div>
          ) : (
            <div className="pt-3 space-y-1 border-t border-white/5">
              <div className="flex items-center gap-2 px-2 py-2 mb-1">
                <div className="w-7 h-7 bg-rm-red/20 rounded-sm flex items-center justify-center">
                  <span className="font-rajdhani font-bold text-xs text-rm-red">{user.username?.[0]?.toUpperCase()}</span>
                </div>
                <span className="text-rm-red font-rajdhani font-bold text-sm">{user.username}</span>
              </div>
              {(user.role === 'seller' || user.role === 'admin') && (
                <Link to="/dashboard" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-2 py-2 text-sm text-rm-muted hover:text-white transition-colors">
                  <LayoutDashboard size={14} /> Dashboard
                </Link>
              )}
              {user.role === 'admin' && (
                <Link to="/admin" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-2 py-2 text-sm text-rm-muted hover:text-white transition-colors">
                  <Shield size={14} /> Admin Panel
                </Link>
              )}
              <button onClick={() => { handleLogout(); setMenuOpen(false); }}
                className="flex items-center gap-2 px-2 py-2 text-sm text-rm-red w-full">
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
